import React, { useCallback, useEffect, useRef, useState } from "react";
import { signIn, getCurrentUser, fetchUserAttributes, signOut, fetchAuthSession } from "@aws-amplify/auth";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { authorLoginReturnPath } from "../../services/groupInvitations";
import { configureAuth } from "../../auth/authConfig";
import { persistAuthType } from "../../auth/authTypeStorage";
import { withSessionLock } from "../../auth/sessionQueue";
import { Eye, EyeOff } from "lucide-react";
import PartnerHeader from "./PartnerHeader";

const sessionKeys = ['sub', 'custom:firstname', 'custom:lastname', 'custom:membershipactive', 'custom:onboardingcomplete', 'custom:subdomain', 'idToken'];
const inputClass = "w-full px-4 py-2 min-h-[44px] border border-bookish-purple/15 rounded-lg bg-white focus:border-bookish-purple/40 focus:ring-2 focus:ring-bookish-purple/10 focus:outline-none";
function loginError(err) {
  const messages = {
    NotAuthorizedException: 'We couldn’t sign you in. Check your email and password, or reset your password below.',
    UserNotFoundException: 'We couldn’t sign you in. Check your email and password, or create an Author account below.',
    UserNotConfirmedException: 'Your email still needs verification. Use “Finish verifying your email” below to continue.',
    PasswordResetRequiredException: 'Please reset your password using the link below, then sign in again.',
    TooManyRequestsException: 'Too many attempts. Please wait a little before trying again.',
    LimitExceededException: 'Too many attempts. Please wait a little before trying again.',
    IncompleteAuthorProfile: 'Your Author account is missing profile details. Contact Lady Liza below for help finishing setup.',
    MissingSession: 'We couldn’t open your author session. Please try signing in again.',
    AdditionalSignInStep: 'Sign-in needs an additional verification step. Contact Lady Liza below for help accessing your account.',
  };
  return messages[err?.name] || 'We couldn’t complete sign-in. Please try again, or contact Lady Liza below if it continues.';
}

export default function LoginAuthor({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState('Checking your author session…');
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const requestLock = useRef(true);
  const mounted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = authorLoginReturnPath(location.search);
  const signupPath = returnTo.startsWith('/publishing-groups/join?') ? `/signupauthor${returnTo.slice(returnTo.indexOf('?'))}` : '/signupauthor';
  const resetPath = `/forgotpassword?${new URLSearchParams({ type: 'author', returnTo })}`;

  const readAuthorSession = useCallback(async () => {
    const user = await getCurrentUser();
    const attributes = await fetchUserAttributes();
    if (!(attributes['custom:firstname'] || '').trim() && !(attributes['custom:lastname'] || '').trim()) {
      await signOut();
      sessionKeys.forEach(key => sessionStorage.removeItem(key));
      if (mounted.current) setUser(null);
      throw Object.assign(new Error(), { name: 'IncompleteAuthorProfile' });
    }
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw Object.assign(new Error(), { name: 'MissingSession' });
    return { user, attributes, token };
  }, [setUser]);

  const openPortal = useCallback(({ user, attributes, token }) => {
    if (!mounted.current) return;
    persistAuthType('author');
    sessionStorage.setItem('sub', attributes.sub || '');
    for (const key of sessionKeys.filter(key => key.startsWith('custom:'))) {
      sessionStorage.setItem(key, attributes[key] || (['custom:membershipactive', 'custom:onboardingcomplete'].includes(key) ? 'false' : ''));
    }
    sessionStorage.setItem('idToken', token);
    setBusy('Opening your Author Portal…');
    setUser(user);
    navigate(returnTo, { replace: true });
  }, [navigate, returnTo, setUser]);

  useEffect(() => {
    mounted.current = true;
    let active = true;
    requestLock.current = true;
    setBusy('Checking your author session…');
    withSessionLock(async () => {
      if (!active) return;
      configureAuth('author');
      return readAuthorSession();
    }).then(session => { if (active && session) openPortal(session); }).catch(err => {
      if (active && err.name !== 'UserUnAuthenticatedException' && err.name !== 'NotAuthorizedException') setError(loginError(err));
    }).finally(() => {
      if (active) { requestLock.current = false; setBusy(''); }
    });
    return () => { active = false; mounted.current = false; };
  }, [readAuthorSession, openPortal]);

  async function handleLogin(event) {
    event.preventDefault();
    if (requestLock.current || !event.currentTarget.reportValidity()) return;
    requestLock.current = true;
    setError(''); setNeedsVerification(false); setBusy('Signing in…');
    try {
      const session = await withSessionLock(async () => {
        configureAuth('author');
        let result;
        try { result = await signIn({ username: email.trim(), password }); }
        catch (err) {
          if (err.name !== 'UserAlreadyAuthenticatedException') throw err;
          await signOut();
          sessionKeys.forEach(key => sessionStorage.removeItem(key));
          if (mounted.current) setUser(null);
          result = await signIn({ username: email.trim(), password });
        }
        if (!result.isSignedIn) {
          const next = result.nextStep?.signInStep;
          throw Object.assign(new Error(), { name: next === 'CONFIRM_SIGN_UP' ? 'UserNotConfirmedException' : next === 'RESET_PASSWORD' ? 'PasswordResetRequiredException' : 'AdditionalSignInStep' });
        }
        return readAuthorSession();
      });
      openPortal(session);
    } catch (err) {
      if (mounted.current) { setError(loginError(err)); setNeedsVerification(err.name === 'UserNotConfirmedException'); }
    } finally {
      requestLock.current = false;
      if (mounted.current) setBusy('');
    }
  }

  return <div className="min-h-screen bg-bookish-cream text-bookish-royal">
    <PartnerHeader audience="author" />
    <main className="account-page max-w-lg mx-auto">
      <div className="max-w-md mx-auto w-full bg-white rounded-2xl shadow-[0_12px_40px_rgba(63,37,107,0.10)] border border-bookish-purple/8 p-6 sm:p-8 text-center">
        <p className="text-sm uppercase tracking-widest mb-3">Author Portal</p>
        <h1 className="text-3xl font-serif font-bold mb-3">Welcome back, Author</h1>
        <p className="mb-6">Your next chapter is waiting.</p>
        <form onSubmit={handleLogin} aria-label="Author login" aria-busy={Boolean(busy)}>
          <fieldset disabled={Boolean(busy)} className="space-y-4">
            <div><label className="account-field" htmlFor="loginauthor-email">Email</label><input id="loginauthor-email" autoComplete="email" type="email" className={inputClass} value={email} onChange={e => setEmail(e.target.value.trim())} required /></div>
            <div><label className="account-field" htmlFor="loginauthor-password">Password</label><div className="relative">
              <input id="loginauthor-password" autoComplete="current-password" type={showPassword ? 'text' : 'password'} className={`${inputClass} pr-14`} value={password} onChange={e => setPassword(e.target.value)} required />
              <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 text-bookish-royal"><span aria-hidden="true">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</span></button>
            </div></div>
            <p role="alert" className="text-sm">{error}</p>
            <p role="status" className="text-sm">{busy}</p>
            <button type="submit" disabled={Boolean(busy)} className="w-full bg-bookish-royal text-white py-2 min-h-[44px] rounded-lg hover:bg-bookish-purpleAccent font-semibold disabled:opacity-60">{busy || 'Sign in'}</button>
          </fieldset>
        </form>
        <Link className="block py-3 underline font-semibold" to={resetPath}>Forgot password?</Link>
        {needsVerification && <Link className="block py-3 underline" to={signupPath} state={{ resumeVerification: true, email: email.trim() }}>Finish verifying your email</Link>}
        <p className="text-sm mt-3">New to the Circle? <Link className="underline" to={signupPath}>Create an Author account</Link></p>
        <Link className="block py-3 underline" to="/landingpage">Back to Bookish Circle</Link>
        <p className="text-sm mt-3">Need a hand? <a className="underline" href="mailto:author@hayleyraejohnson.com">Contact Lady Liza</a>.</p>
      </div>
    </main>
  </div>;
}
